"use client"

import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react'
import { createClient } from '@/utils/supabase/client'
import { isTauri, loadSessionFromTauriStore, clearSessionFromTauriStore, waitForCacheReady } from '@/utils/supabase/tauri-storage'
import type { User, Session } from '@supabase/supabase-js'
import { identifyUser, resetUser } from '@/lib/posthog'

interface AuthContextType {
  user: User | null
  session: Session | null
  isLoading: boolean
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signUp: (email: string, password: string, inviteToken?: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  signInWithGoogle: (inviteToken?: string) => Promise<{ error: Error | null }>
  signInWithApple: (inviteToken?: string) => Promise<{ error: Error | null }>
  /** ゲスト (匿名ユーザー) として開始。Supabase 側で匿名ログインが有効なときだけ成功する */
  signInAsGuest: () => Promise<{ error: Error | null }>
  /** 匿名ユーザーにメール+パスワードを付けて本アカウントへ昇格 (確認メールが飛ぶ) */
  convertGuestWithEmail: (email: string, password: string) => Promise<{ error: Error | null }>
  /** 匿名ユーザーに OAuth の ID を紐付けて昇格 (Dashboard の manual linking が必要) */
  linkGuestIdentity: (provider: 'google' | 'apple') => Promise<{ error: Error | null }>
  deleteAccount: () => Promise<{ error: Error | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Web環境で localStorage に保存済みのセッションを同期的に読む。
// これにより、リロード時に getSession() の解決を待たずに UI を即座にレンダーできる。
// Tauri 環境はカスタムストレージのため対象外（従来通り非同期で初期化する）。
function getInitialSessionFromStorage(): Session | null {
  if (typeof window === 'undefined') return null
  if (isTauri()) return null
  try {
    const raw = window.localStorage.getItem('sb-auth-token')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.access_token && parsed?.user) {
      return parsed as Session
    }
    return null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const initialSession = typeof window !== 'undefined' ? getInitialSessionFromStorage() : null
  const [user, setUser] = useState<User | null>(initialSession?.user ?? null)
  const [session, setSession] = useState<Session | null>(initialSession)
  // Web でキャッシュ済みセッションがあれば即時表示。なければ従来通りローディング。
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    if (isTauri()) return true
    return initialSession === null
  })
  const initializedRef = useRef(false)
  const deepLinkListenerRef = useRef(false)
  const deepLinkProcessedRef = useRef(false)

  const supabase = createClient()

  // Deep Linkイベントを処理（Tauri環境用）
  useEffect(() => {
    if (!isTauri() || deepLinkListenerRef.current) return
    deepLinkListenerRef.current = true

    const setupDeepLinkListener = async () => {
      try {
        const tauriWindow = window as any
        if (!tauriWindow.__TAURI__?.event?.listen) {
          return
        }

        // Deep Linkイベントをリッスン
        const unlisten = await tauriWindow.__TAURI__.event.listen('deep-link', async (event: { payload: string }) => {
          // 既に処理済みの場合はスキップ
          if (deepLinkProcessedRef.current) {
            return
          }

          const url = event.payload

          // auth/callbackパスでない場合はスキップ
          if (!url.includes('auth/callback')) {
            return
          }

          // URLからトークンを抽出
          // 形式: donezo://auth/callback#access_token=xxx&refresh_token=xxx...
          // または: donezo://auth/callback?code=xxx
          try {
            const urlObj = new URL(url)
            const hashParams = new URLSearchParams(urlObj.hash.substring(1))
            const queryParams = urlObj.searchParams

            const accessToken = hashParams.get('access_token')
            const refreshToken = hashParams.get('refresh_token')
            const code = queryParams.get('code')

            if (accessToken && refreshToken) {
              // 処理中フラグを立てる
              deepLinkProcessedRef.current = true

              const { error } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken,
              })

              if (error) {
                console.error('[AuthProvider] Failed to set session:', error)
                deepLinkProcessedRef.current = false
              } else {
                // 認証成功後にホームページへリダイレクト（静的エクスポート用にindex.htmlを指定）
                setTimeout(() => {
                  window.location.href = '/index.html'
                }, 100)
              }
            } else if (code) {
              // 処理中フラグを立てる
              deepLinkProcessedRef.current = true

              const { error } = await supabase.auth.exchangeCodeForSession(code)

              if (error) {
                console.error('[AuthProvider] Failed to exchange code:', error)
                deepLinkProcessedRef.current = false
              } else {
                // 認証成功後にホームページへリダイレクト（静的エクスポート用にindex.htmlを指定）
                setTimeout(() => {
                  window.location.href = '/index.html'
                }, 100)
              }
            }
          } catch (e) {
            console.error('[AuthProvider] Failed to parse deep link URL:', e)
          }
        })

        return unlisten
      } catch (e) {
        console.error('[AuthProvider] Failed to setup deep link listener:', e)
      }
    }

    setupDeepLinkListener()
  }, [supabase])

  useEffect(() => {
    // 二重初期化防止
    if (initializedRef.current) return
    initializedRef.current = true

    let subscriptionRef: { unsubscribe: () => void } | null = null
    let authInitialized = false

    const initializeAuth = async () => {
      // Tauri環境の場合、まずキャッシュの初期化を確実に待つ
      // これにより、Supabaseクライアント作成時の競合状態を解消
      if (isTauri()) {
        await waitForCacheReady()

        const storedSession = await loadSessionFromTauriStore()

        // ストアにセッションがある場合、Supabaseクライアントに手動で設定
        // （Supabaseクライアント初期化時にキャッシュが空のため、手動設定が必要）
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession)
            if (sessionData.access_token && sessionData.refresh_token) {
              const { data, error } = await supabase.auth.setSession({
                access_token: sessionData.access_token,
                refresh_token: sessionData.refresh_token,
              })
              if (error) {
                console.error('[AuthProvider] Failed to set session:', error)
              } else {
                setSession(data.session)
                setUser(data.session?.user ?? null)
                setIsLoading(false)
                authInitialized = true
                return
              }
            }
          } catch (e) {
            console.error('[AuthProvider] Failed to parse stored session:', e)
          }
        }
      }

      // 初期セッションの取得（Web環境、またはTauriでセッションがない場合）
      const { data: { session } } = await supabase.auth.getSession()
      setSession(session)
      setUser(session?.user ?? null)
      setIsLoading(false)
      authInitialized = true
    }

    // 認証状態の変更を監視（initializeAuthの前に設定するが、初期化完了まではスキップ）
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // 初期化完了前のINITIAL_SESSIONイベントはスキップ（initializeAuthで処理する）
      if (!authInitialized && event === 'INITIAL_SESSION') {
        return
      }

      // 初期化完了後の状態変更のみ反映
      if (authInitialized) {
        setSession(session)
        setUser(session?.user ?? null)
        setIsLoading(false)
      }
    })
    subscriptionRef = subscription

    initializeAuth()

    return () => {
      if (subscriptionRef) {
        subscriptionRef.unsubscribe()
      }
    }
  }, [supabase])

  // PCスリープ復帰・タブ再アクティブ時にセッションをリフレッシュ（Web / Tauri 共通）
  // autoRefreshToken はバックグラウンド/スリープ中にタイマーが止まるため、
  // 復帰時に期限切れ（または期限が近い）トークンを先回りで更新し、
  // 期限切れ JWT のまま DB クエリが飛んで PGRST301 になるのを防ぐ。
  useEffect(() => {
    // 期限切れ判定のマージン（秒）。完全失効後だけでなく、
    // 復帰直後にクエリが飛ぶまでの猶予として期限が近い場合も更新する。
    const REFRESH_MARGIN_SECONDS = 60

    const handleVisibilityChange = async () => {
      if (!document.hidden && session) {
        // アプリが再びアクティブになったらセッションの有効期限を確認
        const expiresAt = session.expires_at
        if (expiresAt && expiresAt * 1000 < Date.now() + REFRESH_MARGIN_SECONDS * 1000) {
          console.log('[AuthProvider] Session expired or near expiry after resume, refreshing...')
          try {
            const { data, error } = await supabase.auth.refreshSession()
            if (error) {
              console.error('[AuthProvider] Session refresh failed:', error)
            } else if (data.session) {
              setSession(data.session)
              setUser(data.session.user)
            }
          } catch (e) {
            console.error('[AuthProvider] Session refresh error:', e)
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [supabase, session])

  useEffect(() => {
    if (user?.id) {
      identifyUser(user.id, user.email ? { email: user.email } : undefined)
    } else {
      resetUser()
    }
  }, [user?.id, user?.email])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { error: error as Error | null }
  }

  const signUp = async (email: string, password: string, inviteToken?: string) => {
    const origin =
      typeof window !== 'undefined' ? window.location.origin : ''
    // 招待経由なら、確認メールのリンクが招待ページに戻るよう emailRedirectTo を付与
    // （別デバイスでメールを開いた場合でも招待先に着地できる）
    const emailRedirectTo = inviteToken
      ? `${origin}/join/${encodeURIComponent(inviteToken)}`
      : undefined
    // トークンを user_metadata にも保存する。localStorage(TTL あり) や
    // リダイレクト URL と違いサーバー側に残るため、メール確認が何時間後でも
    // 別デバイスで開いても、初回ログイン時に自動参加できる
    // (usePendingInviteJoin がフォールバックとして参照し、使用後にクリアする)
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: inviteToken
        ? { emailRedirectTo, data: { pending_invite_token: inviteToken } }
        : undefined,
    })
    return { error: error as Error | null }
  }

  const signOut = async () => {
    // ログアウト前にフラグをリセット（データ消失防止）
    // 注意: バックアップデータは消さない（次回ログイン時の復元用）
    if (typeof window !== 'undefined') {
      localStorage.removeItem('dbInitialized')
    }

    // Tauri環境の場合、ストアからセッションを明示的に削除（完了を待つ）
    if (isTauri()) {
      await clearSessionFromTauriStore()
    }

    // セッションが存在する場合のみAPIコールを試みる（403エラー回避）
    if (session) {
      try {
        // scope: 'local'で現在のデバイスのみログアウト
        await supabase.auth.signOut({ scope: 'local' })
      } catch {
        // セッション期限切れなどのエラーは無視
      }
    }

    // 常にローカル状態をクリア
    setSession(null)
    setUser(null)
  }

  const signInWithGoogle = async (inviteToken?: string) => {
    // Tauri環境かどうかを検出
    const tauriWindow = window as any
    const isTauriEnv = typeof window !== 'undefined' && tauriWindow.__TAURI__ !== undefined

    if (isTauriEnv) {
      try {
        // OAuth URLを取得
        const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: 'donezo://auth/callback',
            skipBrowserRedirect: true,
          },
        })

        if (oauthError || !data?.url) {
          return { error: oauthError as Error | null }
        }

        // ネイティブのASWebAuthenticationSessionを使用
        const result = await tauriWindow.__TAURI__.core.invoke('native_oauth', {
          url: data.url,
          callbackScheme: 'donezo',
        })

        if (!result.success) {
          if (result.cancelled) {
            return { error: null } // ユーザーがキャンセルした場合はエラーなし
          }
          return { error: new Error(result.error || 'Google sign in failed') }
        }

        // コールバックURLからトークンを抽出
        if (result.callbackUrl) {
          const urlObj = new URL(result.callbackUrl)
          const hashParams = new URLSearchParams(urlObj.hash.substring(1))
          const queryParams = urlObj.searchParams

          const accessToken = hashParams.get('access_token')
          const refreshToken = hashParams.get('refresh_token')
          const code = queryParams.get('code')

          if (accessToken && refreshToken) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            })

            if (sessionError) {
              console.error('[AuthProvider] setSession error:', sessionError)
              return { error: sessionError as Error }
            }
          } else if (code) {
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)

            if (exchangeError) {
              return { error: exchangeError as Error }
            }
          }
        }

        return { error: null }
      } catch (e: any) {
        console.error('[AuthProvider] Google sign in error:', e)
        return { error: new Error(e.message || 'Google sign in failed') }
      }
    } else {
      // Web環境の場合、通常のリダイレクト
      // 本番環境では環境変数のURLを使用、なければwindow.location.originを使用
      const baseUrl = process.env.NEXT_PUBLIC_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '')
      const callbackUrl = inviteToken
        ? `${baseUrl}/auth/callback?invite=${encodeURIComponent(inviteToken)}`
        : `${baseUrl}/auth/callback`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
        },
      })
      return { error: error as Error | null }
    }
  }

  const signInWithApple = async (inviteToken?: string) => {
    // Tauri環境かどうかを検出
    const tauriWindow = window as any
    const isTauriEnv = typeof window !== 'undefined' && tauriWindow.__TAURI__ !== undefined

    if (isTauriEnv) {
      try {
        // ネイティブのSign in with Appleを使用
        const result = await tauriWindow.__TAURI__.core.invoke('sign_in_with_apple')

        if (!result.success) {
          // ユーザーがキャンセルした場合
          if (result.error?.includes('canceled') || result.error?.includes('cancelled')) {
            return { error: null }
          }
          return { error: new Error(result.error || 'Apple sign in failed') }
        }

        // SupabaseにIDトークンを送信して認証
        if (result.identityToken) {
          const { error: signInError } = await supabase.auth.signInWithIdToken({
            provider: 'apple',
            token: result.identityToken,
          })

          if (signInError) {
            console.error('[AuthProvider] Supabase sign in error:', signInError)
            return { error: signInError as Error }
          }

          return { error: null }
        } else {
          return { error: new Error('No identity token received from Apple') }
        }
      } catch (e: any) {
        console.error('[AuthProvider] Apple sign in error:', e)
        return { error: new Error(e.message || 'Apple sign in failed') }
      }
    } else {
      // Web環境の場合、通常のリダイレクト
      const baseUrl = process.env.NEXT_PUBLIC_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '')
      const callbackUrl = inviteToken
        ? `${baseUrl}/auth/callback?invite=${encodeURIComponent(inviteToken)}`
        : `${baseUrl}/auth/callback`
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: callbackUrl,
        },
      })
      return { error: error as Error | null }
    }
  }

  // ===== ゲストモード (匿名認証) =====
  // 判定・文言は lib/guest-mode.ts。ここは supabase-js の呼び出しだけ

  const signInAsGuest = async () => {
    const { error } = await supabase.auth.signInAnonymously()
    return { error: error as Error | null }
  }

  const convertGuestWithEmail = async (email: string, password: string) => {
    // 匿名ユーザーの updateUser は「メール確認が済んだ時点で is_anonymous=false」になる。
    // パスワードは即時に付く
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const { error } = await supabase.auth.updateUser(
      { email, password },
      { emailRedirectTo: origin ? `${origin}/auth/callback` : undefined },
    )
    return { error: error as Error | null }
  }

  const linkGuestIdentity = async (provider: 'google' | 'apple') => {
    const baseUrl = process.env.NEXT_PUBLIC_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '')
    const { error } = await supabase.auth.linkIdentity({
      provider,
      options: { redirectTo: `${baseUrl}/auth/callback` },
    })
    return { error: error as Error | null }
  }

  const deleteAccount = async () => {
    try {
      // Supabase Edge Function を呼び出してアカウントを削除
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        return { error: new Error('Not authenticated') }
      }

      const response = await fetch(`${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/delete-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { error: new Error(errorData.error || 'Failed to delete account') }
      }

      // ローカル状態をクリア
      if (typeof window !== 'undefined') {
        localStorage.clear()
      }
      setSession(null)
      setUser(null)

      return { error: null }
    } catch (e: any) {
      return { error: new Error(e.message || 'Failed to delete account') }
    }
  }

  return (
    <AuthContext.Provider value={{
      user,
      session,
      isLoading,
      signIn,
      signUp,
      signOut,
      signInWithGoogle,
      signInWithApple,
      signInAsGuest,
      convertGuestWithEmail,
      linkGuestIdentity,
      deleteAccount,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
